import React, { useState } from "react";
import { FolderPlus } from "lucide-react";
import AssetItem from "./AssetItem";
import FileUploadZone from "../components/uploads/FileUploadZone";
import UploadProgressDisplay from "../components/common/UploadProgressDisplay";
import {
  ProjectInfoService,
  ProjectInfoFields,
} from "../components/services/ProjectInfoService";

const PvUploadManager = ({
  items = [],
  onDelete,
  onUpload,
  projectId,
  onPvUpdate,
  loading,
  showUpload,
  setShowUpload,
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState(null);

  const handlePvFileUpload = async (files) => {
    if (files.length === 0) return;

    const file = files[0];

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Uploading file...",
      }));
      // newviewer/master/72/
      const destPath = `newviewer/master/${projectId}/${file.name}`;
      // const destPath = `projects/${projectId}/pv/${file.name}`;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("destPath", destPath);
      formData.append("overwrite", "true");

      const token = localStorage.getItem("token");

      const apiRootOperational =
        process.env.REACT_APP_API_ROOT_OPERATIONAL || "";
      const response = await fetch(
        `${apiRootOperational}/api/files/upload-form`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Complete",
      }));

      const baseFilename = file.name.replace(/\.[^/.]+$/, "");

      const apiRoot = process.env.REACT_APP_API_ROOT || "";
      const projectInfoService = new ProjectInfoService(apiRoot, token);

      const updatedList = [...items, baseFilename];

      await projectInfoService.updateListField(
        projectId,
        "pv_panels",
        updatedList,
        { overwrite: true }
      );

      setUploadStatus("success");

      if (onPvUpdate) {
        onPvUpdate(updatedList);
      }

      setTimeout(() => {
        setUploadProgress({});
        setUploadStatus(null);
        setShowUpload(false);
      }, 1500);
    } catch (error) {
      console.error("Error uploading PV file:", error);
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Failed",
      }));
      setUploadStatus("error");
    }
  };

  if (items.length === 0 && !loading) {
    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200 flex justify-between">
          <span>PV Panel Assets</span>
          <button
            onClick={() => setShowUpload(true)}
            className="px-3 py-1 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
          >
            <FolderPlus size={14} className="inline mr-1" />
            Add PV File
          </button>
        </h3>
        <div className="p-4 bg-gray-50 rounded-md text-gray-500 text-sm">
          No PV files available
        </div>

        {showUpload && (
          <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <h4 className="text-sm font-medium mb-2">Add New PV Panel File</h4>
            <FileUploadZone
              onFilesSelect={handlePvFileUpload}
              accept=".glb"
              acceptedTypes={["GLB"]}
            />
            <UploadProgressDisplay progress={uploadProgress} />

            {uploadStatus && (
              <div
                className={`mt-2 p-2 ${
                  uploadStatus === "success"
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                } border rounded-lg`}
              >
                <p className="text-xs text-center">
                  {uploadStatus === "success"
                    ? "PV file added successfully!"
                    : "Error uploading file. Please try again."}
                </p>
              </div>
            )}

            <button
              onClick={() => setShowUpload(false)}
              className="mt-2 px-3 py-1 text-gray-600 text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
        <span>PV Panel Assets</span>
      </h3>

      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <AssetItem
            key={index}
            item={item}
            projectId={projectId}
            onDelete={onDelete}
            onUpload={onUpload}
            apiField="pv_panels"
          />
        ))}
      </div>

      {showUpload && (
        <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-sm font-medium mb-2">Add New PV Panel File</h4>
          <FileUploadZone
            onFilesSelect={handlePvFileUpload}
            accept=".glb"
            acceptedTypes={["GLB"]}
          />
          <UploadProgressDisplay progress={uploadProgress} />

          {uploadStatus && (
            <div
              className={`mt-2 p-2 ${
                uploadStatus === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              } border rounded-lg`}
            >
              <p className="text-xs text-center">
                {uploadStatus === "success"
                  ? "PV file added successfully!"
                  : "Error uploading file. Please try again."}
              </p>
            </div>
          )}

          <button
            onClick={() => setShowUpload(false)}
            className="mt-2 px-3 py-1 text-gray-600 text-sm"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default PvUploadManager;
